import * as React from 'react';
import { flow, forEach, get } from 'lodash';
import { change, formValueSelector, getFormSyncErrors, InjectedFormProps, reduxForm, submit } from 'redux-form';
import { connect } from 'react-redux';
import { IStore } from '../../../../redux/IStore';
import { Form } from 'react-bootstrap';
import { QuoteStepButtons } from '../Components/QuoteStepButtons';
import { PriceDisplay } from '../Components/PriceDisplay';
import { IAuthState } from '../../../../services/auth/models';
import {
  IBilling,
  IChannel,
  IDocument,
  IOptionMap,
  IPremium,
  IProduct,
  IQuotes,
  ISignposted,
  IStateCities,
  ITraveller,
  TripType
} from '../../../../data/api/models';
import { ICollapsableComponent, IQuoteActions, IQuoteState } from '../../../../services/quote/models';
import { bindActionCreators } from 'redux';
import { actions as quoteActions, actions } from '../../../../services/quote/reducer';
import { push } from 'react-router-redux';
import { FooterPriceDisplay } from '../Components/FooterPriceDisplay';
import { TermsAndConditions } from '../Components/TermsAndConditions';
import { MedicalDetails } from './Components/MedicalLoad';
import { SectionName } from './Components/SectionName';
import { DocumentPreferences } from './Components/DocumentPreferences';
import { renewalTypeSelectionEnum } from '../../components/YourRenewal';
import { PaymentTermsAndConditions } from './Components/PaymentTermsAndConditions';
import { PaymentConsent } from './Components/PaymentConsent';
import { QuoteSummary } from './Components/QuoteSummary';
import { II18nMessages } from '../../../../services/branding/models';
import { CalculationError } from '../Components/CalculationError';
import { IVeriskState } from '../../../../services/verisk/models';
import { Voucher } from '../StartNewQuote/Components/Voucher';
import { shouldIgnoreCardPermissions } from '../../../../helpers/Quote';
import { scrollToTop } from '../../../../helpers/TravelApplication';
import { quoteHasMedical } from '../../../../helpers/signpost';
import { PaymentErrorMessage } from './Components/PaymentErrorMessage';

interface IQuotePaymentProps extends InjectedFormProps {
  auth: IAuthState;
  premium: IPremium;
  travellers: ITraveller[];
  tripType: TripType;
  quote: IQuoteState;
  options: IOptionMap;
  quotes: IQuotes;
  groupType: string;
  region: string;
  schemeId: string,
  stateCities: IStateCities[];
  product: IProduct;
  communications: any;
  i18n: II18nMessages;
  synchronousError: any;
  documents: IDocument[];
  signposted: ISignposted;
  renewalStatus: renewalTypeSelectionEnum;
  documentPack: string;
  permissions: boolean;
  channel: IChannel;
  billing: IBilling;
  errorMessage: any;
  quoteReference: string;
  quoteState: IQuoteState;
  destinationsValue: string[];
  stateCitiesValue: string[];
  startDate: string;
  endDate: string;
  onBack: (key?: string) => void;
  verisk: IVeriskState;
  medicalHistory: boolean;
  campaignCode: any;
  maxTripDuration: any;
  medicalsDeclared?: boolean;
  quoteForEdit: boolean;
  collapsable: ICollapsableComponent
  actions: {
    dispatch: any;
    quote: IQuoteActions;
    push(path: string);
    submit(name: string);
  }

  change(field: string, value: any): void;

  renderDefinition(name: string, useProvidedString?: boolean): any;
}

class QuotePayment extends React.Component<IQuotePaymentProps, any> {

  constructor(props) {
    super(props);
    const { change } = props;
    change('permissions', true);
    this.recalculate = this.recalculate.bind(this);
    scrollToTop();
  }

  componentDidUpdate(prevProps: Readonly<IQuotePaymentProps>, prevState: Readonly<any>, snapshot?: any) {
    const { change, quoteState, schemeId, travellers } = this.props;
    let selectedPremium;
    if (schemeId && quoteState.quotes) {
      selectedPremium = this.getSelectedPremium();
      const hasMedicalAdded = quoteHasMedical(null, travellers);
      change('signposted', {
        medicalDecline: false,
        medicalLoad: get(selectedPremium, 'isSignposted', false) && hasMedicalAdded,
      });
    }
  }

  private getSelectedPremium = (): any => {
    const { actions, schemeId, tripType } = this.props;
    return actions.quote.getQuoteSelectedPremium(schemeId, tripType);
  }

  private recalculateSchemes = () => {
    this.recalculate(true);
  }

  private readonly recalculate = (shouldCalculateAll) => {
    const { actions, change } = this.props;
    change('calculateAllSchemes', shouldCalculateAll);
    actions.quote.calculate(null, true, true);
  }

  private handleGoBack = () => {
    const { change, onBack } = this.props;
    change('schemeId', '');
    onBack();
  }

  public render() {
    const {
      handleSubmit,
      travellers,
      submitting,
      submitFailed,
      valid,
      schemeId,
      tripType,
      documents,
      signposted,
      product,
      permissions,
      quoteState,
      change,
      channel,
      billing,
      quote,
      region,
      destinationsValue,
      premium,
      options,
      startDate,
      endDate,
      documentPack,
      groupType,
      verisk,
      actions,
      campaignCode,
      onBack,
      synchronousError,
      medicalHistory,
      quoteReference,
      medicalsDeclared,
      maxTripDuration,
      renderDefinition,
      collapsable,
      errorMessage,
    } = this.props;

    /**
     * Field medicalsDeclared has been removed from the form validation (isRequired) so other components in the page can rely on form.valid to validate their data.
     */
    const checkMedicalBeforeSubmit = (name: string): Promise<any> => {
      this.setState({ isQuoteSubmitted: true });
      if (medicalsDeclared) {
        return actions.submit(name);
      } else {
        const element = document.getElementById('medicals-declared');
        element.scrollIntoView();
        return new Promise((resolve, reject) => {
          resolve();
        });
      }
    }

    const selectedPremium = this.getSelectedPremium();
    const shouldShowRenewalSection = tripType === 'annual';
    const ignorePermissions = shouldIgnoreCardPermissions();

    if (!permissions && !ignorePermissions) {
      change('renewalStatus', renewalTypeSelectionEnum.MANUAL)
    }

    if (get(quoteState, 'calculationError.type')) {
      return (
        <div className="steps-container error">
          <CalculationError calculationError={quoteState.calculationError}/>
        </div>
      )
    }

    return (
      <Form horizontal={true} onSubmit={handleSubmit} autoComplete="off">
        <div className="steps-container">

          <PriceDisplay
            title={'Here\'s your final price for'}
            tripType={tripType}
            premium={selectedPremium}
            showInfo={true}
            infoText={'Please check your cover details before payment'}
            quoteReference={quoteReference}
            {...this.props}
          />

          <PaymentErrorMessage
            errorMessage={errorMessage}
            submitFailed={submitFailed}
            valid={valid}
            quoteReference={quoteReference}
          />

          <div style={{ marginTop: '10px' }}/>

          <Voucher
            quoteState={quoteState}
            actions={actions}
            applyCallback={this.recalculateSchemes}
            campaignCode={campaignCode}
            {...this.props}
          />

          <SectionName imageName={'quote-summary-black.svg'} title={'Quote Summary'}/>

          <QuoteSummary
            travellers={travellers}
            tripType={tripType}
            region={region}
            destinationsValue={destinationsValue}
            premium={premium}
            schemeId={schemeId}
            options={options}
            product={product}
            startDate={startDate}
            endDate={endDate}
            groupType={groupType}
            verisk={verisk}
            recalculate={this.recalculate}
            synchronousError={synchronousError}
            renderDefinition={renderDefinition}
            channel={channel}
            onBack={onBack}
            maxTripDuration={maxTripDuration}
            medicalHistory={medicalHistory}
            collapsable={collapsable}
            {...this.props}
          />

          <SectionName imageName={'important-statements.svg'} title={'Medical details'}/>

          <MedicalDetails
            travellers={travellers}
            product={product}
            channel={channel}
            premium={premium}
            isCalculating={quoteState.isCalculating}
            handleEdit={onBack}
            isSignposted={get(signposted, 'medicalLoad')}
            isQuoteSubmitted={!!this.state?.isQuoteSubmitted}
            {...this.props}
          />

          <div style={{ marginTop: '20px' }}/>

          <div style={{ color: '#0045A0', backgroundColor: '#e3f1fe', fontWeight: 'bold', padding: '10px' }}>
            Please make sure you don’t already have cover in place.
          </div>

          <div style={{ marginTop: '20px' }}/>

          <PaymentTermsAndConditions/>

          <div style={{ marginTop: '20px' }}/>

          <DocumentPreferences {...this.props} documentPack={documentPack}/>

          <div style={{ marginTop: '20px' }}/>

          <PaymentConsent
            travellers={travellers}
            shouldShowRenewalSection={shouldShowRenewalSection}
            channel={channel}
            ignorePermissions={ignorePermissions}
            permissions={permissions}
            billing={billing}
            {...this.props}/>

          <FooterPriceDisplay premium={selectedPremium} tripType={tripType}/>

          <TermsAndConditions documents={documents}/>

          <div style={{ marginTop: '50px', display: medicalsDeclared === false ? 'none' : '' }}>
            <QuoteStepButtons
              submitting={submitting}
              hasBackButton={true}
              handleBack={this.handleGoBack}
              submitTitle={'CONFIRM AND PAY'}
              submitFailed={submitFailed}
              valid={valid}
              disable={quote.waitForPurchaseToComplete}
              submitButtonType={'validateButton'}
              synchronousError={synchronousError}
              checkForErrors={true}
              submit={checkMedicalBeforeSubmit}
            />
          </div>

        </div>
      </Form>
    );
  }
}

let selector;
export default flow([
  reduxForm({
    form: 'quote',
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: false,
  }),
  (component) => {
    selector = formValueSelector('quote');

    return component;
  },
  connect(
    (state: IStore) => {
      const synchronousError = getFormSyncErrors('quote')(state);
      const errorMessage = state.quote.errorMessage;
      const quotes = state.quote.quotes;
      const quoteState = state.quote;
      const travellers = selector(state, 'travellers');
      const tripType = selector(state, 'tripType');
      const groupType = selector(state, 'groupType');
      const documents = selector(state, 'documents');
      const signposted = selector(state, 'signposted');
      const schemeId = selector(state, 'schemeId');
      const communications = selector(state, 'communications');
      const renewalStatus = selector(state, 'renewalStatus');
      const permissions = selector(state, 'permissions');
      const billing = selector(state, 'billing');
      const quoteReference = selector(state, 'quoteReference');
      const region = selector(state, 'region');
      const options = selector(state, 'options');
      const startDate = selector(state, 'startDate');
      const endDate = selector(state, 'endDate');
      const destinationsValue = selector(state, 'destinations');
      const stateCitiesValue = selector(state, 'stateCities');
      const documentPack = selector(state, 'documentPack');
      const campaignCode = selector(state, 'campaignCode');
      const medicalHistory = selector(state, 'medicalHistory');
      const medicalsDeclared = selector(state, 'medicalsDeclared');
      const maxTripDuration = selector(state, 'maxTripDuration');

      let premium = null;

      forEach(quotes, (tripTypes, level) => {
        forEach(tripTypes, (quotedPremium) => {
          if (quotedPremium.scheme.id === schemeId) {
            premium = quotedPremium;
          }
        });
      });
      return {
        auth: state.auth,
        i18n: state.branding.i18n,
        verisk: state.verisk,
        quoteForEdit: state.quote.quoteForEdit || false,
        collapsable: state.quote.collapsableComponents,
        quoteState,
        synchronousError,
        travellers,
        tripType,
        groupType,
        documents,
        signposted,
        schemeId,
        communications,
        renewalStatus,
        permissions,
        billing,
        errorMessage,
        quoteReference,
        region,
        destinationsValue,
        premium,
        options,
        startDate,
        endDate,
        documentPack,
        stateCitiesValue,
        campaignCode,
        medicalHistory,
        maxTripDuration,
        medicalsDeclared
      };
    },
    (dispatch) => ({
      actions: {
        dispatch,
        submit: bindActionCreators(submit, dispatch),
        calculate: bindActionCreators(actions.calculate, dispatch),
        push: bindActionCreators(push, dispatch),
        quote: bindActionCreators({ ...quoteActions }, dispatch),
        change: bindActionCreators(change, dispatch),
      },
    }),
  ),
])(QuotePayment);
